import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useState} from "react";
import DateRangePicker from "../../components/DateRangePicker.jsx";
import UpdateDropdown from "../../components/dashboard/UpdateDropdown.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useRenderCurrencyValue, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../utils/useUserSettings.js";
import MarketingAndManagerSwitch from "../../components/filters/MarketingAndManagerSwitch.jsx";
import SeeMore from "../../components/SeeMore.jsx";
import LoadingCard from "../../components/LoadingCard.jsx";
import OverviewValue from "../../components/dashboard/OverviewValue.jsx";
import moment from "moment-timezone";
import Graph from "../../components/graphs/Graph.jsx";
import {GraphScale, GraphType} from "../../components/graphs/Graph.types.ts";
import DataGrid2 from "../../components/datagrid/DataGrid2.jsx";
import {useGetBackgroundColor} from "../../utils/graphColors.js";
import {useBrands} from "../../utils/brands/branding.js";
import BrandLogo from "../../utils/brands/BrandLogo.jsx";

export default function NewComponents({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {
        since, until,
    } = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let renderCurrencyValue = useRenderCurrencyValue();
    let renderPercentValue = useRenderPercentValue();

    let getBackgroundColor = useGetBackgroundColor();

    let [userSettings] = useUserSettings();

    let [shopBasicStatisticsData, setShopBasicStatisticsData] = useState(null);
    let [turnoverByDomainData, setTurnoverByDomainData] = useState(null);
    let [statisticsBySourceData, setStatisticsBySourceData] = useState(null);
    let [countryShippingAndPaymentData, setCountryShippingAndPaymentData] = useState(null);

    let brands = useBrands();

    useEffect(() => {
        setShopBasicStatisticsData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopBasicStatistics({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedDomainIds: [],
                    selectedCentreIds: [],

                }
            })
            .then(setShopBasicStatisticsData)
            .catch((e) => et(e).then(toast.error));


    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),]);

    useEffect(() => {
        setTurnoverByDomainData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopTurnoverByDomainStatistics({
                projectId: project.id, dateRangeInput: {
                    since: since.format('YYYY-MM-DD'), until: until.format('YYYY-MM-DD'),
                }
            })
            .then(setTurnoverByDomainData)
            .catch((e) => et(e).then(toast.error));


    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),]);

    useEffect(() => {
        setStatisticsBySourceData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopChannelsStatisticsBySource({
                projectId: project.id, dateRangeInput: {
                    since: since.format('YYYY-MM-DD'), until: until.format('YYYY-MM-DD'),
                }
            })
            .then(setStatisticsBySourceData)
            .catch((e) => et(e).then(toast.error));


    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),]);

    useEffect(() => {
        setCountryShippingAndPaymentData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopCountryPaymentAndShippingStatistics({
                projectId: project.id, dateRangeInput: {
                    since: since.format('YYYY-MM-DD'), until: until.format('YYYY-MM-DD'),
                }
            })
            .then(setCountryShippingAndPaymentData)
            .catch((e) => et(e).then(toast.error));


    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),]);

    /**
     * @type {GraphData|null}
     */
    let turnoverByDomainInTimeBarChartData = useMemo(() => {
        if (turnoverByDomainData === null) {
            return null;
        }

        let firstDomain = turnoverByDomainData.dataByDomain[0];

        if (firstDomain === null) {
            return null;
        }

        return {
            labels: Object.keys(firstDomain.turnoverNetGranular),
            type: GraphType.Bar,
            scale: GraphScale.Linear,
            tickRender: (label) => {
                return moment(label).format('MM.YYYY');
            },
            tooltipRender: (context) => {
                return [moment(context.label).format('MM.YYYY'), context.dataset.label, renderCurrencyValue({
                    amount: Object.values(context.dataset.data)[context.dataIndex] * 100, currency: project.currency,
                }),];
            },
            datasets: Object.values(turnoverByDomainData.dataByDomain).map(domain => {


                let selectedData = userSettings?.isMarketing ? domain.turnoverGrossGranular : domain.turnoverNetGranular;

                let selectedDataValues = {};

                for (let index in selectedData) {
                    selectedDataValues[index] = selectedData[index].amount / 100;
                }

                return {
                    label: domain.projectDomain.hostName,
                    data: selectedDataValues,
                };
            })
                .filter((dataset) => {
                    const sum = Object.values(dataset.data).reduce((acc, value) => acc + value, 0);
                    return sum !== 0;
                })
        };
    }, [turnoverByDomainData, userSettings?.isMarketing]);


    /**
     * @type {{source: object, graph: GraphData}[]|null}
     */
    let topChannelGraphDataList = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        const sources = [
            statisticsBySourceData.topChannel1,
            statisticsBySourceData.topChannel2,
            statisticsBySourceData.topChannel3,
            statisticsBySourceData.otherChannels,
        ];

        const data = [];

        sources.map((source) => {
            const labels = Object.keys(source.spendMarketing.valueGranular);
            data.push({
                source: source,
                graph: {
                    labels: labels,
                    type: GraphType.Line,
                    scale: GraphScale.Linear,
                    showLegend: false,
                    showTooltip: false,
                    datasets: [
                        source.spendMarketing.valueGranular,
                        source.spendMarketing.valueGranularPreviousPeriod,
                    ].map((granularData, index) => {
                        let isCurrent = index === 0;

                        let selectedDataValues = {};

                        const values = Object.values(granularData);

                        for (let granularIndex in values) {
                            const label = labels[granularIndex];
                            selectedDataValues[label] = values[granularIndex].amount / 100;
                        }

                        return {
                            label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                            data: selectedDataValues,
                            gray: isCurrent === false,
                        };
                    })
                },
            });
        });

        return data;
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let turnoverInTimeLineChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranular
            : shopBasicStatisticsData.turnoverNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.turnoverNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;
            let values = Object.values(valueGranular);
            let data = {};
            for (let index in values) {
                const label = labels[index];
                data[label] = values[index].amount / 100;
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            tickRender: (label) => {
                return moment(label).format('DD.MM.YYYY');
            },
            tooltipRender: (context) => {
                return [
                    moment(context.label).format('DD.MM.YYYY'),
                    context.dataset.label,
                    renderCurrencyValue({
                        amount: Object.values(context.dataset.data)[context.dataIndex] * 100,
                        currency: project.currency,
                    }),
                ];
            },
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let pnoKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranular
            : shopBasicStatisticsData.pnoNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.pnoNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;
            let values = Object.values(valueGranular);
            let data = {};
            for (let index in values) {
                const label = labels[index];
                data[label] = values[index];
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            tickRender: (label) => {
                return moment(label).format('DD.MM.YYYY');
            },
            tooltipRender: (context) => {
                return [
                    moment(context.label).format('DD.MM.YYYY'),
                    context.dataset.label,
                    renderPercentValue(Object.values(context.dataset.data)[context.dataIndex]),
                ];
            },
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let aovKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.averageOrderValueGross.valueGranular
            : shopBasicStatisticsData.averageOrderValueNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.averageOrderValueGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.averageOrderValueNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;
            let values = Object.values(valueGranular);
            let data = {};
            for (let index in values) {
                const label = labels[index];
                data[label] = values[index].amount / 100;
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            tickRender: (label) => {
                return moment(label).format('DD.MM.YYYY');
            },
            tooltipRender: (context) => {
                return [
                    moment(context.label).format('DD.MM.YYYY'),
                    context.dataset.label,
                    renderCurrencyValue({
                        amount: Object.values(context.dataset.data)[context.dataIndex] * 100,
                        currency: project.currency,
                    }),
                ];
            },
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    let spendOverviewSourceData = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        return [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    let spendComparisonGraphData = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        let sources = [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];

        let labels = sources.map((source) => source.sourceName);

        let dataSource = {
            data: [],
            backgroundColor: [],
        };

        for (const sourceIndex in sources) {
            const source = sources[sourceIndex];

            if (source.spendMarketing.value.amount === '0') {
                continue;
            }

            dataSource.data.push(source.spendMarketing.value.amount / 100);
            dataSource.backgroundColor.push(getBackgroundColor(sourceIndex));
        }

        return {
            labels: labels,
            type: GraphType.Pie,
            scale: GraphScale.Linear,
            showLegend: false,
            tooltipRender: (context) => {
                return [
                    labels[context.dataIndex],
                    renderCurrencyValue({
                        amount: Object.values(context.dataset.data)[context.dataIndex] * 100,
                        currency: project.currency,
                    }),
                ];
            },
            datasets: [dataSource],
        };
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    return <WideSecureLayout
        title={t('route.new-components')}
        actions={<div className={'filter-btn flex justify-center gap-1 flex-wrap sm:justify-end'}>
            <UpdateDropdown/>
            <MarketingAndManagerSwitch/>
            <DateRangePicker/>
        </div>}
    >
        <div className={'flex gap-2 w-full'}>
            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.base-statistical-data')}
                className={'mt-2 w-8/12'}
            >
                <div className={'flex flex-col justify-between h-full'}>
                    {shopBasicStatisticsData && <div className="flex flex-wrap">
                        <OverviewValue
                            title={t('orders.turnover')}
                            value={userSettings?.isMarketing ? renderCurrencyValue(shopBasicStatisticsData.turnoverGross.value, true) : renderCurrencyValue(shopBasicStatisticsData.turnoverNet.value, true)}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.turnoverGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.turnoverNet.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('dashboard.grid.profit')}
                            value={userSettings?.isMarketing ? renderCurrencyValue(shopBasicStatisticsData.gainGross.value, true) : renderCurrencyValue(shopBasicStatisticsData.gainNet.value, true)}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.gainGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.gainGross.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('route.marketing')}
                            value={renderCurrencyValue(shopBasicStatisticsData.spendMarketing.value, true)}
                            percentChange={shopBasicStatisticsData.spendMarketing.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('dashboard.orders-short')}
                            value={userSettings?.isMarketing ? shopBasicStatisticsData.orderAmountGross.value : shopBasicStatisticsData.orderAmountNet.value}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.orderAmountGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.orderAmountNet.percentageChangeAgainstPreviousPeriod}
                            last={true}
                        />
                    </div>}
                    {turnoverInTimeLineChartData && <Graph
                        data={turnoverInTimeLineChartData}
                        style={{'maxHeight': '200px'}}
                    />}
                </div>
            </LoadingCard>

            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.kpi')}
                className={'mt-2 w-4/12'}
            >
                {shopBasicStatisticsData && <div className="flex flex-col flex-wrap">
                    <OverviewValue
                        title={t('dashboard.total-shop-pno')}
                        value={renderPercentValue(userSettings?.isMarketing ? shopBasicStatisticsData.pnoGross.value : shopBasicStatisticsData.pnoNet.value)}
                        last={true}
                    />
                    {pnoKpiChartData && <Graph
                        data={pnoKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                    <OverviewValue
                        title={'AOV'}
                        value={renderCurrencyValue(userSettings?.isMarketing ? shopBasicStatisticsData.averageOrderValueGross.value : shopBasicStatisticsData.averageOrderValueNet.value)}
                        last={true}
                    />
                    {aovKpiChartData && <Graph
                        data={aovKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                </div>}
            </LoadingCard>
        </div>
        <div className={'flex gap-2 w-full'}>
            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.turnover-in-time')}
                className={'mt-2 w-full'}
            >
                {turnoverByDomainInTimeBarChartData && <Graph
                    data={turnoverByDomainInTimeBarChartData}
                    style={{'maxHeight': '200px'}}
                />}
            </LoadingCard>
        </div>
        <div className={'flex gap-2 w-full'}>
            {[0, 1, 2, 3].map((index) =>
                <LoadingCard
                    loading={topChannelGraphDataList === null}
                    className={'mt-2 w-1/4'}
                    key={'sourceGraph' + index}
                >
                    {topChannelGraphDataList && topChannelGraphDataList[index] && <>
                        <OverviewValue
                            title={topChannelGraphDataList[index].source.sourceName}
                            value={renderCurrencyValue(topChannelGraphDataList[index].source.spendMarketing.value, true)}
                            last={true}
                        />
                        <Graph
                            data={topChannelGraphDataList[index].graph}
                            style={{'maxHeight': '80px'}}
                        />
                    </>}
                </LoadingCard>
            )}
        </div>

        <div className={'flex gap-2 w-full mt-2'}>
            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-8/12'}
                title={t('orders.top-channels')}
            >
                {statisticsBySourceData && <SeeMore heightClass={'h-60'}>
                    <DataGrid2
                        columns={[{
                            name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 2,
                        }, {
                            name: 'orderAmount',
                            textAlign: 'end',
                            header: t('dashboard.grid.spend-marketing'),
                            render: (row) => userSettings?.isMarketing ? row.data.orderAmountGross.value : row.data.orderAmountNet.value,
                        }, {
                            name: 'averageOrderValue', textAlign: 'end', header: 'AOV', render: (row) => {
                                return renderCurrencyValue(userSettings?.isMarketing ? row.data.averageOrderValueNet.value : row.data.averageOrderValueNet.value, true,);
                            },
                        }, {
                            name: 'spendMarketing',
                            textAlign: 'end',
                            header: t('dashboard.grid.spend-marketing'),
                            render: (row) => {
                                return renderCurrencyValue(row.data.spendMarketing.value, true,);
                            },
                        }, {
                            name: 'turnover', textAlign: 'end', header: t('dashboard.turnover'), render: (row) => {
                                return renderCurrencyValue(userSettings?.isMarketing ? row.data.turnoverGross.value : row.data.turnoverNet.value, true,);
                            },
                        }]}
                        dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                        hasTotal={false}
                    />
                </SeeMore>}

            </LoadingCard>


            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-4/12'}
                title={t('orders.top-other-channels')}
            >
                {statisticsBySourceData && <DataGrid2
                    columns={[{
                        name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 1,
                    }, {
                        name: 'amount',
                        textAlign: 'end',
                        header: t('dashboard.grid.spend-marketing'),
                        render: (row) => {
                            return renderCurrencyValue(row.data.spendMarketing.value, true,);
                        },
                    }]}
                    dataSource={Object.values(statisticsBySourceData.topOtherCategoriesChannels)}
                />}
            </LoadingCard>
        </div>

        <div className={'flex gap-2 w-full mt-2'}>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-country')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'countryName',
                            header: t('orders.country'),
                            defaultFlex: 1,
                            render: (row) => row.data.country.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(12),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.turnover'), render: (row) => {
                                return userSettings?.isMarketing ? renderCurrencyValue(row.data.turnoverGross, true) : renderCurrencyValue(row.data.turnoverNet, true)
                            },
                        }]}
                        dataSource={countryShippingAndPaymentData.countryStatistics}
                        truncateFirstColumn={false}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-payment')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'paymentMethodName',
                            header: t('orders.payment-method'),
                            defaultFlex: 1,
                            render: (row) => row.data.paymentMethod.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(6),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.amount'), render: (row) => {
                                return userSettings?.isMarketing ? row.data.amountGross : row.data.amountNet
                            },
                        }]}
                        truncateFirstColumn={true}
                        dataSource={countryShippingAndPaymentData.paymentStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-shipping')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[
                            {
                                name: 'shippingMethodText',
                                header: t('orders.shipping-method'),
                                defaultFlex: 1,
                                render: (row) => row.data.shippingMethod.name,
                                percentage: {
                                    width: 50,
                                    color: getBackgroundColor(0),
                                },
                            },
                            {
                                name: 'amount',
                                textAlign: 'end',
                                header: t('orders.amount'),
                                render: (row) => {
                                    return userSettings?.isMarketing ? row.data.amountGross : row.data.amountNet
                                },
                            }
                        ]
                        }
                        dataSource={countryShippingAndPaymentData.shippingStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
        </div>


        <div className={'flex gap-2'}>
            <div className={'w-5/12 mt-2'}>
                <LoadingCard
                    loading={spendComparisonGraphData === null}
                    className={'w-full'}
                    title={t('orders.spend-comparison')}
                >
                    {spendComparisonGraphData && <Graph
                        data={spendComparisonGraphData}
                        style={{'width': '100%', 'aspectRatio': '1', maxHeight: '310px'}}
                    />}
                </LoadingCard>
            </div>

            <div className={'w-7/12 mt-2'}>
                <LoadingCard
                    loading={spendOverviewSourceData === null}
                    className={'w-full'}
                    title={t('orders.spend-overview')}
                >
                    {spendOverviewSourceData && <SeeMore heightClass={'h-[264px]'}>
                        <DataGrid2
                            columns={[
                                {
                                    name: 'sourceName',
                                    header: t('dashboard.channel'),
                                    render: ({ data }) => {
                                        const sourceName = data.sourceName;
                                        const brandObj = brands[sourceName];  // This is the object from useBrands(), or undefined
                                        const displayName = brandObj?.name || sourceName;  // Use .name if object, else fallback to sourceName (string)
                                        return (
                                            <div className={'flex gap-2 items-center'}>
                                                <BrandLogo
                                                    brand={brandObj || sourceName}  // Pass object if exists, else string (adjust if BrandLogo requires object)
                                                    size={8}
                                                />
                                                {displayName}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    name: 'spendMarketing',
                                    textAlign: 'end',
                                    header: t('dashboard.grid.spend-marketing'),
                                    render: (row) => {
                                        return renderCurrencyValue(row.data.spendMarketing.value, true,);
                                    },
                                },
                                {
                                    name: 'turnover',
                                    textAlign: 'end',
                                    header: t('dashboard.turnover'),
                                    render: (row) => {
                                        return renderCurrencyValue(userSettings?.isMarketing ? row.data.turnoverGross.value : row.data.turnoverNet.value, true,);
                                    },
                                }
                            ]}
                            dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                            hasTotal={false}
                            truncateFirstColumn={false}
                        />
                    </SeeMore>}

                </LoadingCard>
            </div>
        </div>
    </WideSecureLayout>;
}